import { mapIntegrationProviderFromDTO } from '@/modules/common/mappers/integration.mapper';
import { IntegrationProviderDTO } from '@/modules/common/types/dto/response/integration-response';
import { getIntegrationDisplayName } from '@/modules/common/utils/integration-icons';
import { AuthenticatedRoutes, SettingsRoutes } from '@/router/routes';

type NotificationArgs = Record<string, string>;

interface NotificationDescription {
  key: string;
  args: NotificationArgs;
}

export interface NotificationTypeConfig {
  titleKey: string;
  descriptionKey: string;
  /** Picks the description variant and its args when they depend on optional args. */
  resolveDescription?: (args: NotificationArgs) => NotificationDescription;
  getActionUrl?: (actionArgs: NotificationArgs) => string;
}

/**
 * Description key + interpolation args of a notification.
 * Falls back to `descriptionKey` with the raw args.
 */
export const getNotificationDescription = (
  config: NotificationTypeConfig,
  args: NotificationArgs
): NotificationDescription =>
  config.resolveDescription?.(args) ?? { key: config.descriptionKey, args };

/** A known provider wire value (e.g. "JIRA") becomes its display name; anything else is shown as sent. */
const toIntegrationDisplayName = (integration: string): string =>
  Object.values(IntegrationProviderDTO).includes(integration as IntegrationProviderDTO)
    ? getIntegrationDisplayName(
        mapIntegrationProviderFromDTO(integration as IntegrationProviderDTO)
      )
    : integration;

export const NOTIFICATION_TYPE_CONFIG: Record<string, NotificationTypeConfig> = {
  // Assignment
  ASSIGNMENT_CREATED: {
    titleKey: 'types.assignmentCreated.title',
    descriptionKey: 'types.assignmentCreated.description',
    getActionUrl: args => AuthenticatedRoutes.ProjectDetails.replace(':projectId', args.projectId),
  },
  ASSIGNMENT_DELETED: {
    titleKey: 'types.assignmentDeleted.title',
    descriptionKey: 'types.assignmentDeleted.description',
  },
  ASSIGNMENT_USER_CHANGED: {
    titleKey: 'types.assignmentUserChanged.title',
    descriptionKey: 'types.assignmentUserChanged.description',
  },
  ASSIGNMENT_DETAILS_UPDATED: {
    titleKey: 'types.assignmentDetailsUpdated.title',
    descriptionKey: 'types.assignmentDetailsUpdated.description',
    getActionUrl: args => AuthenticatedRoutes.ProjectDetails.replace(':projectId', args.projectId),
  },
  ASSIGNMENT_PROJECT_CHANGED: {
    titleKey: 'types.assignmentProjectChanged.title',
    descriptionKey: 'types.assignmentProjectChanged.description',
    getActionUrl: args => AuthenticatedRoutes.ProjectDetails.replace(':projectId', args.projectId),
  },
  ASSIGNMENT_ZERO_RATE: {
    titleKey: 'types.assignmentZeroRate.title',
    descriptionKey: 'types.assignmentZeroRate.description',
    getActionUrl: args => AuthenticatedRoutes.ProjectDetails.replace(':projectId', args.projectId),
  },

  // Project
  PROJECT_MANAGER_ASSIGNED: {
    titleKey: 'types.projectManagerAssigned.title',
    descriptionKey: 'types.projectManagerAssigned.description',
    getActionUrl: args => AuthenticatedRoutes.ProjectDetails.replace(':projectId', args.projectId),
  },
  PROJECT_MANAGER_UNASSIGNED: {
    titleKey: 'types.projectManagerUnassigned.title',
    descriptionKey: 'types.projectManagerUnassigned.description',
  },
  PROJECT_ARCHIVED: {
    titleKey: 'types.projectArchived.title',
    descriptionKey: 'types.projectArchived.description',
  },
  PROJECT_DELETED: {
    titleKey: 'types.projectDeleted.title',
    descriptionKey: 'types.projectDeleted.description',
  },
  PROJECT_BILLING_UPDATED: {
    titleKey: 'types.projectBillingUpdated.title',
    descriptionKey: 'types.projectBillingUpdated.description',
    getActionUrl: args =>
      `${AuthenticatedRoutes.ProjectDetails.replace(':projectId', args.projectId)}?tab=billing`,
  },

  // Team Member
  TEAM_MEMBER_MANAGER_ASSIGNED: {
    titleKey: 'types.teamMemberManagerAssigned.title',
    descriptionKey: 'types.teamMemberManagerAssigned.description',
    getActionUrl: args => AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', args.memberId),
  },
  TEAM_MEMBER_MANAGER_UNASSIGNED: {
    titleKey: 'types.teamMemberManagerUnassigned.title',
    descriptionKey: 'types.teamMemberManagerUnassigned.description',
    getActionUrl: args => AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', args.memberId),
  },
  TEAM_MEMBER_ARCHIVED: {
    titleKey: 'types.teamMemberArchived.title',
    descriptionKey: 'types.teamMemberArchived.description',
    getActionUrl: args => AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', args.memberId),
  },
  TEAM_MEMBER_UNARCHIVED: {
    titleKey: 'types.teamMemberUnarchived.title',
    descriptionKey: 'types.teamMemberUnarchived.description',
    getActionUrl: args => AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', args.memberId),
  },
  TEAM_MEMBER_DELETED: {
    titleKey: 'types.teamMemberDeleted.title',
    descriptionKey: 'types.teamMemberDeleted.description',
  },
  TEAM_MEMBER_BILLING_UPDATED: {
    titleKey: 'types.teamMemberBillingUpdated.title',
    descriptionKey: 'types.teamMemberBillingUpdated.description',
    getActionUrl: args =>
      `${AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', args.memberId)}?tab=billing`,
  },
  TEAM_MEMBER_CAPACITY_UPDATED: {
    titleKey: 'types.teamMemberCapacityUpdated.title',
    descriptionKey: 'types.teamMemberCapacityUpdated.description',
    getActionUrl: args =>
      `${AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', args.memberId)}?tab=capacity`,
  },

  // Client
  CLIENT_MANAGER_ASSIGNED: {
    titleKey: 'types.clientManagerAssigned.title',
    descriptionKey: 'types.clientManagerAssigned.description',
    getActionUrl: args => AuthenticatedRoutes.ClientDetails.replace(':clientId', args.clientId),
  },
  CLIENT_MANAGER_UNASSIGNED: {
    titleKey: 'types.clientManagerUnassigned.title',
    descriptionKey: 'types.clientManagerUnassigned.description',
    getActionUrl: args => AuthenticatedRoutes.ClientDetails.replace(':clientId', args.clientId),
  },
  CLIENT_ARCHIVED: {
    titleKey: 'types.clientArchived.title',
    descriptionKey: 'types.clientArchived.description',
    getActionUrl: args => AuthenticatedRoutes.ClientDetails.replace(':clientId', args.clientId),
  },
  CLIENT_DELETED: {
    titleKey: 'types.clientDeleted.title',
    descriptionKey: 'types.clientDeleted.description',
  },

  // Org Settings
  ORG_RATE_MANAGEMENT_UPDATED: {
    titleKey: 'types.orgRateManagementUpdated.title',
    descriptionKey: 'types.orgRateManagementUpdated.description',
    getActionUrl: () => SettingsRoutes.Finance,
  },
  ORG_DEPARTMENTS_UPDATED: {
    titleKey: 'types.orgDepartmentsUpdated.title',
    descriptionKey: 'types.orgDepartmentsUpdated.description',
    getActionUrl: () => SettingsRoutes.Departments,
  },
  ORG_SENIORITIES_UPDATED: {
    titleKey: 'types.orgSenioritiesUpdated.title',
    descriptionKey: 'types.orgSenioritiesUpdated.description',
    getActionUrl: () => SettingsRoutes.Seniorities,
  },
  ORG_ROLES_UPDATED: {
    titleKey: 'types.orgRolesUpdated.title',
    descriptionKey: 'types.orgRolesUpdated.description',
    getActionUrl: () => SettingsRoutes.Roles,
  },

  // Time Tracking — Unassigned
  // No getActionUrl: NotificationItemUnassignedTime owns its own navigation so it can
  // verify the member/project still exists before leaving the tray.
  TIME_TRACKED_WITHOUT_ASSIGNMENT: {
    titleKey: 'types.timeTrackedWithoutAssignment.title',
    descriptionKey: 'types.timeTrackedWithoutAssignment.description',
  },

  // Time Tracking Reminders
  // NOTE: descriptionKey defaults to the "booked" wording — used by the toast
  // renderer and any fallback path. The inbox item (NotificationItemTimeTrackingReminder)
  // renders its own toggle and picks between descriptionBooked/descriptionAvailability.
  TIME_TRACKING_WEEKLY_REMINDER: {
    titleKey: 'types.timeTrackingWeeklyReminder.title',
    descriptionKey: 'types.timeTrackingWeeklyReminder.descriptionBooked',
    getActionUrl: () => AuthenticatedRoutes.TimeTracking,
  },
  TIME_TRACKING_MONTHLY_REMINDER: {
    titleKey: 'types.timeTrackingMonthlyReminder.title',
    descriptionKey: 'types.timeTrackingMonthlyReminder.descriptionBooked',
    getActionUrl: () => AuthenticatedRoutes.TimeTracking,
  },

  // Time Off Requests
  TIME_OFF_REQUEST_CREATED: {
    titleKey: 'types.timeOffRequestCreated.title',
    descriptionKey: 'types.timeOffRequestCreated.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffApprovalDetails.replace(':requestId', args.requestId),
  },
  TIME_OFF_REQUEST_CANCELLED: {
    titleKey: 'types.timeOffRequestCancelled.title',
    descriptionKey: 'types.timeOffRequestCancelled.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffApprovalDetails.replace(':requestId', args.requestId),
  },
  TIME_OFF_REQUEST_APPROVED: {
    titleKey: 'types.timeOffRequestApproved.title',
    descriptionKey: 'types.timeOffRequestApproved.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffRequestDetails.replace(':requestId', args.requestId),
  },
  TIME_OFF_REQUEST_DECLINED: {
    titleKey: 'types.timeOffRequestDeclined.title',
    descriptionKey: 'types.timeOffRequestDeclined.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffRequestDetails.replace(':requestId', args.requestId),
  },
  TIME_OFF_REQUEST_QUEUED: {
    titleKey: 'types.timeOffRequestQueued.title',
    descriptionKey: 'types.timeOffRequestQueued.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffApprovalDetails.replace(':requestId', args.requestId),
  },
  TIME_OFF_REQUEST_READY_FOR_REVIEW: {
    titleKey: 'types.timeOffRequestReadyForReview.title',
    descriptionKey: 'types.timeOffRequestReadyForReview.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffApprovalDetails.replace(':requestId', args.requestId),
  },
  TIME_OFF_REQUEST_PARTIALLY_APPROVED: {
    titleKey: 'types.timeOffRequestPartiallyApproved.title',
    descriptionKey: 'types.timeOffRequestPartiallyApproved.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TimeOffRequestDetails.replace(':requestId', args.requestId),
  },

  // Upcoming Vacation
  UPCOMING_TEAM_MEMBER_VACATION: {
    titleKey: 'types.upcomingTeamMemberVacation.title',
    descriptionKey: 'types.upcomingTeamMemberVacation.description',
    getActionUrl: args =>
      AuthenticatedRoutes.TeamMemberTimeOffRequestDetails.replace(
        ':memberId',
        args.memberId
      ).replace(':requestId', args.requestId),
  },

  // Integrations
  TEAM_MEMBERS_NEED_REVIEW: {
    titleKey: 'types.teamMembersNeedReview.title',
    descriptionKey: 'types.teamMembersNeedReview.description',
    resolveDescription: args =>
      args.integration
        ? {
            key: 'types.teamMembersNeedReview.descriptionWithIntegration',
            args: { ...args, integration: toIntegrationDisplayName(args.integration) },
          }
        : { key: 'types.teamMembersNeedReview.description', args },
    getActionUrl: () => AuthenticatedRoutes.DataReconciliation,
  },

  // Worklog / Integrations
  WORKLOG_APPROVED_SYNC_CONFLICT: {
    titleKey: 'types.worklogApprovedSyncConflict.title',
    descriptionKey: 'types.worklogApprovedSyncConflict.description',
    getActionUrl: () => AuthenticatedRoutes.TimeTracking,
  },
};
